import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IGroup } from '~/types/group'

import {
  addRolesToGroup,
  addUsersToGroup,
  createGroup,
  deleteRolesFromGroup,
  deleteUsersFromGroup,
  getGroup,
  getGroups,
  updateGroupGeneralInformation,
} from '~/api/groups'

////////// KEYS //////////

export const keys = {
  all: ['groups'] as const,
  details: (groupId?: string) => [...keys.all, 'details', groupId] as const,
  list: () => [...keys.all, 'list'] as const,
}

////////// QUERIES //////////

export const useGroupsListQuery = (): UseQueryResult<{
  data: IGroup[]
  total: number
}> => {
  return useQuery({ queryFn: getGroups, queryKey: keys.list() })
}

export const useGroupDetailsQuery = (groupId?: string): UseQueryResult<IGroup> => {
  return useQuery({
    queryFn: !!groupId ? () => getGroup(groupId) : skipToken,
    queryKey: keys.details(groupId),
  })
}

////////// MUTATIONS //////////

export const useCreateGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { description: string; groupName: string }
> => {
  return useMutation({ mutationFn: createGroup })
}

export const useUpdateGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { description: string; groupId: string; groupName: string }
> => {
  return useMutation({ mutationFn: updateGroupGeneralInformation })
}

export const useAddUsersToGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { groupId: string; userIds: string[] }
> => {
  return useMutation({ mutationFn: addUsersToGroup })
}

export const useAddRolesToGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { groupId: string; roleIds: string[] }
> => {
  return useMutation({ mutationFn: addRolesToGroup })
}

export const useDeleteRoleFromGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { groupId: string; roleIds: string[] }
> => {
  return useMutation({ mutationFn: deleteRolesFromGroup })
}

export const useDeleteUserFromGroupMutation = (): UseMutationResult<
  IGroup,
  unknown,
  { groupId: string; userId: string }
> => {
  return useMutation({ mutationFn: deleteUsersFromGroup })
}
